<template>
  <section
    v-if="blok"
    v-editable="blok"
    :data-section-uid="blok._uid"
    class="engine-hero"
  >
    <div class="container container--section">
      <div class="engine-hero__wrapper">
        <div class="engine-hero__description">
          <p class="engine-hero__subtitle">
            {{ blok.small_title }}
          </p>
          <h1 class="engine-hero__title">
            {{ blok.title }}
          </h1>
          <p class="engine-hero__text" v-html="blok.subtitle" />
          <BaseButton
            :title="blok.cta_text"
            @click="scrollToForm('#form-section', 40)"
          >
            <template #text>
              {{ blok.cta_text }}
            </template>
          </BaseButton>
        </div>
        <div class="engine-hero__image-wrapper">
          <BaseNuxtImage
            :src="blok?.illustration.filename"
            :alt="blok?.illustration.alt"
            class="engine-hero__image"
          />
        </div>
      </div>
    </div>
    <span class="stars-left" />
    <span class="stars-right" />
  </section>
</template>

<script lang="ts" setup>
import { scrollTo } from '@/utils/helpers'
import type { SbHeroDiagnosticEngineStoryblok } from '@/types'

interface Props {
  blok: SbHeroDiagnosticEngineStoryblok
}
defineProps<Props>()

const scrollToForm = scrollTo
</script>

<style lang="scss" scoped>
.engine-hero {
  padding-top: 60px;
  overflow: hidden;
  position: relative;
  background-color: var(--cobalt-800);

  @media (min-width: $breakpoint-md) {
    padding-top: 80px;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    color: var(--white);
    text-align: center;
  }

  &__title {
    @include heading-2;

    margin-bottom: 24px;
    color: var(--white);

    @media (min-width: $breakpoint-md) {
      @include heading-title;

      margin-bottom: 32px;
    }
  }

  &__subtitle {
    @include heading-subheading;

    margin-bottom: 24px;
    color: var(--white);
  }

  &__text {
    @include paragraph-m;

    margin-bottom: 32px;

    @media (min-width: $breakpoint-md) {
      @include paragraph-l;

      margin-bottom: 40px;
    }
  }

  &__description {
    position: relative;
    z-index: 20;
    max-width: 550px;
    margin: 0 auto;

    @media (min-width: $breakpoint-lg) {
      max-width: 660px;
    }
  }

  &__image-wrapper {
    height: 286px;

    @media (min-width: $breakpoint-md) {
      height: 475px;
    }

    @media (min-width: $breakpoint-lg) {
      height: 515px;
    }
  }

  &__image {
    height: auto;
    position: absolute;
    bottom: -70px;
    left: -120px;
    width: 820px;
    z-index: 10;

    @media (min-width: $breakpoint-md) {
      left: -280px;
      width: 1640px;
    }
  }
}

.stars-left,
.stars-right {
  position: absolute;
  z-index: 10;
  display: none;
  border-radius: 1.5px;

  &::after,
  &::before {
    position: absolute;
    border-radius: 1.5px;
  }

  @media (min-width: $breakpoint-lg) {
    display: block;
  }
}

.stars-left {
  left: 10%;
  top: 25%;
  width: 12px;
  height: 12px;
  background: var(--blue-500);

  &::after {
    content: '';
    left: 47px;
    top: 202px;
    width: 12px;
    height: 12px;
    background: var(--blue-700);
  }

  &::before {
    content: '';
    left: 53px;
    top: -71px;
    width: 6px;
    height: 6px;
    background: var(--blue-500);
  }
}

.stars-right {
  position: absolute;
  right: 10%;
  top: 30%;
  width: 7px;
  height: 7px;
  background: var(--blue-700);

  &::after {
    content: '';
    position: absolute;
    left: 37px;
    top: 95px;
    width: 6px;
    height: 6px;
    background: var(--yellow-500);
  }
}
</style>
